import React, { useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const WIDTHS = {
  xs: 'max-w-sm',
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
};

/**
 * Tailwind modal — replaces MUI Dialog.
 * Slots: title, subtitle, headerActions, children (body), footer.
 */
const Modal = ({
  open,
  onClose,
  title,
  subtitle,
  headerActions = null,
  footer = null,
  maxWidth = 'sm',
  disableClose = false,
  bodyClassName = '',
  className = '',
  children,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !disableClose && onClose) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, disableClose, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-slate-900/50"
        onClick={disableClose ? undefined : onClose}
      />
      <div
        className={`relative flex max-h-[90vh] w-full flex-col rounded-xl bg-white shadow-xl ${
          WIDTHS[maxWidth] || WIDTHS.sm
        } ${className}`}
      >
        {(title || !disableClose) && (
          <div className="flex flex-shrink-0 items-start justify-between gap-3 border-b border-[#E9EDF6] px-5 py-4">
            <div className="min-w-0">
              {title && <h2 className="text-base font-bold text-[#212B36]">{title}</h2>}
              {subtitle && <p className="mt-0.5 text-[13px] text-[#637381]">{subtitle}</p>}
            </div>
            <div className="flex flex-shrink-0 items-center gap-1">
              {headerActions}
              {!disableClose && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close dialog"
                  className="rounded-full p-1.5 text-[#637381] hover:bg-slate-100 hover:text-[#212B36]"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        )}
        <div className={`min-h-0 flex-1 overflow-y-auto px-5 py-4 ${bodyClassName}`}>{children}</div>
        {footer && (
          <div className="flex flex-shrink-0 items-center justify-end gap-2 border-t border-[#E9EDF6] px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
